<template>
  <div class="m-b-15">
    <div class="inline-block">
      <WdsStack align-center gap-sm justify-start wrap>
        <FeatureFlagged name="cancellable-bills">
          <WdsSelect
            v-model="billStatus.data"
            can-deselect
            multi-select
            :list="$options.billStatusOptions"
            :placeholder="$t('Bill status')"
            class="tw-w-[250px]"
            max-labels-visible="1"
            labels-primary
          />

          <template #is-disabled>
            <WdsSelect
              v-model="billStatus.data"
              can-deselect
              multi-select
              :list="$options.billStatusOptionsWithoutCancelled"
              :placeholder="$t('Approval status')"
              class="tw-w-[250px]"
              max-labels-visible="1"
              labels-primary
            />
          </template>
        </FeatureFlagged>

        <WdsSelect
          v-model="timesheetSelection.data"
          can-deselect
          multi-select
          :list="$options.timeSheetOptions"
          :placeholder="$t('Timesheet status')"
          class="tw-w-[250px]"
          max-labels-visible="1"
          labels-primary
        />

        <WdsSelect
          v-model="poNumberSelection.data"
          can-deselect
          multi-select
          :list="$options.poNumberOptions"
          :placeholder="$t('PO Number status')"
          class="tw-w-[250px]"
          max-labels-visible="1"
          labels-primary
        />

        <WdsSelect
          v-if="recruiters.length"
          v-model="selectedRecruiterObjects"
          can-deselect
          multi-select
          :list="recruiterOptions"
          :placeholder="$t('Recruiters')"
          class="tw-w-[250px]"
          max-labels-visible="1"
          labels-primary
        />

        <WdsSelect
          v-if="teamMembers.length"
          v-model="selectedOwnerObjects"
          can-deselect
          multi-select
          :list="teamMemberOptions"
          :placeholder="$t('Hire owners')"
          class="tw-w-[250px]"
          max-labels-visible="1"
          labels-primary
        />

        <WdsSelect
          v-if="currenciesOptions.length > 1"
          v-model="selectedCurrenciesObjects"
          can-deselect
          multi-select
          :list="currenciesOptions"
          :placeholder="$t('Currencies')"
          class="tw-w-[250px]"
          max-labels-visible="1"
          labels-primary
        />

        <a href="#" class="iblock m-l-15 f-12" @click="resetFilters">
          {{ $t('Reset filters') }}
          <i :class="{ loading: isResetting }" class="fa fa-undo" />
        </a>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import { WdsSelect, WdsStack } from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { LocalStorageEncoder } from '@/utils/Encoders'

export default {
  timeSheetOptions: [
    { value: 1, label: $t('Has timesheet(s)') },
    { value: 0, label: $t('Missing timesheet(s)') },
  ],
  poNumberOptions: [
    { value: 1, label: $t('Has PO Number') },
    { value: 0, label: $t('Missing PO Number') },
  ],
  invoiceStatusOptions: [
    { value: 'UNPAID', label: $t('Unpaid') },
    { value: 'OVERDUE', label: $t('Overdue') },
    { value: 'PAID', label: $t('Paid') },
  ],
  billStatusOptions: [
    { value: 'UNAPPROVED', label: $t('Unapproved') },
    { value: 'APPROVED', label: $t('Approved') },
    { value: 'CANCELLED', label: $t('Cancelled') },
  ],
  billStatusOptionsWithoutCancelled: [
    { value: 'UNAPPROVED', label: $t('Unapproved') },
    { value: 'APPROVED', label: $t('Approved') },
  ],
  components: {
    WdsStack,
    FeatureFlagged,
    WdsSelect,
  },
  props: {
    teamMembers: {
      type: Array,
      default: () => [],
    },
    recruiters: {
      type: Array,
      default: () => [],
    },
    currencies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isResetting: false,
      poNumberSelection: new LocalStorageEncoder({
        storageKey: 'po-number',
        decoder: this.decodePoNumberOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      timesheetSelection: new LocalStorageEncoder({
        storageKey: 'timesheet',
        decoder: this.decodeTimesheetOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      invoiceStatus: new LocalStorageEncoder({
        storageKey: 'invoice-status',
        decoder: this.decodeInvoiceOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      billStatus: new LocalStorageEncoder({
        storageKey: 'bill-status',
        decoder: this.decodebillStatusOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      selectedOwnerIds: new LocalStorageEncoder({
        storageKey: 'owners',
        decoder: (data) =>
          data
            ?.split(',')
            .filter((val) => val !== '')
            .map((val) => (val === 'NO_OWNER' ? val : Number(val))) || [],
        defaultVal: [],
      }),
      selectedRecruiterIds: new LocalStorageEncoder({
        storageKey: 'recruiters',
        decoder: (data) =>
          data?.split(',').map((val) => (val === 'NO_RECRUITER' ? val : Number(val))) || [],
        defaultVal: [],
      }),
      selectedCurrenciesCodes: new LocalStorageEncoder({
        storageKey: 'currencies',
        decoder: (data) => data?.split(',') || [],
        defaultVal: [],
      }),
    }
  },
  computed: {
    filter() {
      return {
        invoiceStatus: this.invoiceStatus.data.map((status) => status.value),
        billStatus: this.billStatus.data.map((status) => status.value),
        hasTimesheet: this.hasTimesheet,
        hasPoNumber: this.hasPoNumber,
        // exclude any owners not contained in the options if we have them
        owners: this.selectedOwnerIds.data.filter(
          (id) =>
            id !== 'NO_OWNER' &&
            (this.teamMemberOptions.map((teamMember) => teamMember.value).includes(id) ||
              this.teamMemberOptions.length === 0),
        ),
        currencies: this.selectedCurrenciesCodes.data.filter((name) =>
          this.currenciesOptions.map((currency) => currency.label).includes(name),
        ),
        noOwners: this.selectedOwnerIds.data.some((id) => id === 'NO_OWNER'),
        // exclude any recruiters not contained in the options
        recruiters: this.selectedRecruiterIds.data.filter(
          (id) =>
            id !== 'NO_RECRUITER' &&
            this.recruiterOptions.map((recruiters) => recruiters.value).includes(id),
        ),
        noRecruiters: this.selectedRecruiterIds.data.some((id) => id === 'NO_RECRUITER'),
      }
    },
    hasTimesheet() {
      const timeSheetSelection = this.timesheetSelection.data.map((selection) => selection.value)
      if (
        timeSheetSelection.length < 1 ||
        (timeSheetSelection.includes(1) && timeSheetSelection.includes(0))
      ) {
        return null
      }
      return !!timeSheetSelection.includes(1)
    },
    hasPoNumber() {
      const poNumberSelection = this.poNumberSelection.data.map((selection) => selection.value)
      if (
        poNumberSelection.length < 1 ||
        (poNumberSelection.includes(1) && poNumberSelection.includes(0))
      ) {
        return null
      }
      return !!poNumberSelection.includes(1)
    },
    selectedOwnerObjects: {
      get() {
        return this.teamMemberOptions.filter((teamMember) =>
          this.selectedOwnerIds.data.includes(teamMember.value),
        )
      },
      set(newValue) {
        this.selectedOwnerIds.data = newValue.map((selectedOwner) => selectedOwner.value)
      },
    },
    teamMemberOptions() {
      return this.teamMembers.length
        ? [{ value: 'NO_OWNER', label: $t('Unassigned') }, ...this.teamMembers]
        : []
    },
    selectedRecruiterObjects: {
      get() {
        return this.recruiterOptions.filter((recruiter) =>
          this.selectedRecruiterIds.data.includes(recruiter.value),
        )
      },
      set(newValue) {
        this.selectedRecruiterIds.data = newValue.map(
          (selectedRecruiter) => selectedRecruiter.value,
        )
      },
    },
    selectedCurrenciesObjects: {
      get() {
        return this.currenciesOptions.filter((currency) =>
          this.selectedCurrenciesCodes.data.includes(currency.label),
        )
      },
      set(newValue) {
        this.selectedCurrenciesCodes.data = newValue.map(
          (selectedCurrency) => selectedCurrency.label,
        )
      },
    },
    recruiterOptions() {
      return this.recruiters.length
        ? [{ value: 'NO_RECRUITER', label: $t('No recruiter') }, ...this.recruiters]
        : []
    },
    currenciesOptions() {
      return this.currencies.map((x) => ({ label: x }))
    },
  },
  watch: {
    filter: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.$emit('filters-updated', newVal)
      },
    },
  },
  methods: {
    $t,
    decodeTimesheetOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.timeSheetOptions.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
    decodePoNumberOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.poNumberOptions.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
    decodeInvoiceOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.invoiceStatusOptions.filter((option) =>
        selections.some((selection) => selection === option.value),
      )
    },
    decodebillStatusOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.billStatusOptions.filter((option) =>
        selections.some((selection) => selection === option.value),
      )
    },
    resetFilters() {
      this.isResetting = true
      setTimeout(() => {
        this.isResetting = false
      }, 600)
      this.selectedOwnerIds.data = []
      this.invoiceStatus.data = []
      this.billStatus.data = []
      this.selectedRecruiterIds.data = []
      this.timesheetSelection.data = []
      this.poNumberSelection.data = []
      this.selectedCurrenciesCodes.data = []
    },
  },
}
</script>

<style scoped>
.inline-block {
  display: inline-block !important;
}

@-webkit-keyframes rotation {
  from {
    -webkit-transform: rotate(359deg);
  }
  to {
    -webkit-transform: rotate(0deg);
  }
}

.fa-undo.loading {
  -webkit-animation: rotation 0.3s 1 ease-out;
}
</style>
